import { Routes, Route, Navigate } from 'react-router-dom';
import { lazy, Suspense, type ReactNode } from 'react';
import { ErrorBoundary } from './components/ErrorBoundary';
import { Toaster } from 'sonner';
import AppLayout from './components/layout/AppLayout';
import AdminLayout from './components/admin/AdminLayout';
import AdminGuard from './components/admin/AdminGuard';
import Header from './components/layout/Header';
import BottomNav from './components/layout/BottomNav';
import HomePage from './pages/Home';
import WritePage from './pages/Write';
import LettersPage from './pages/Letters';
import DiscoverPage from './pages/Discover';
import ProfilePage from './pages/Profile';
import { SemanticState } from './components/semantic/SemanticState';

const AboutPage = lazy(() => import('./pages/About'));
const LoginPage = lazy(() => import('./pages/Login'));
const SettingsPage = lazy(() => import('./pages/Settings'));
const NotificationsPage = lazy(() => import('./pages/Notifications'));
const AIPage = lazy(() => import('./pages/AI'));
const DriftPage = lazy(() => import('./pages/Drift'));
const DriftComposePage = lazy(() => import('./pages/DriftCompose'));
const DriftReadPage = lazy(() => import('./pages/DriftRead'));
const DriftConversationPage = lazy(() => import('./pages/DriftConversation'));
const AdminDashboard = lazy(() => import('./pages/admin/Dashboard'));
const AdminUsers = lazy(() => import('./pages/admin/Users'));
const AdminReview = lazy(() => import('./pages/admin/Review'));
const AdminCouriers = lazy(() => import('./pages/admin/Couriers'));
const AdminSettings = lazy(() => import('./pages/admin/Settings'));
const AdminLetters = lazy(() => import('./pages/admin/Letters'));
const AdminReports = lazy(() => import('./pages/admin/Reports'));
const AdminAIConfig = lazy(() => import('./pages/admin/AIConfig'));
const AdminEnvelopes = lazy(() => import('./pages/admin/Envelopes'));
const NotFoundPage = lazy(() => import('./pages/NotFound'));
const SemanticVisualSystemPage = lazy(() => import('./pages/visual-system/SemanticVisualSystem'));

function RouteLoading() {
  return <SemanticState compact kind="loading" title="正在打开页面" />;
}

interface FullWidthLayoutProps {
  children: ReactNode;
}

/** Full-width layout: Header + BottomNav but no max-width container. */
function FullWidthLayout({ children }: FullWidthLayoutProps) {
  return (
    <div className="flex min-h-[var(--litopia-layout-100dvh)] flex-col bg-letter-paper">
      <Header />
      <main id="opp-main-content" className="opp-shell-main flex-1 md:pb-8">{children}</main>
      <BottomNav />
    </div>
  );
}

function App() {
  return (
    <div className="opp-app-root" data-opp-runtime-root>
      <ErrorBoundary>
        <Suspense fallback={<RouteLoading />}>
          <Routes>
        {/* Standalone pages (own layout) */}
        <Route
          path="/about"
          element={<AboutPage />}
        />
        <Route
          path="/login"
          element={<LoginPage />}
        />
        <Route
          path="/design-system"
          element={<SemanticVisualSystemPage />}
        />

        {/* Full-width layout (no max-width constraint) */}
        <Route path="/me" element={<FullWidthLayout><ProfilePage /></FullWidthLayout>} />

        {/* App layout routes (Header + BottomNav + max-w container) */}
        <Route element={<AppLayout />}>
          <Route path="/" element={<HomePage />} />
          <Route path="/write" element={<WritePage />} />
          <Route path="/letters" element={<LettersPage />} />
          <Route path="/discover" element={<DiscoverPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/notifications" element={<NotificationsPage />} />
          <Route path="/ai" element={<AIPage />} />
          <Route path="/drift" element={<DriftPage />} />
          <Route path="/drift/compose" element={<DriftComposePage />} />
          <Route path="/drift/read" element={<DriftReadPage />} />
          <Route path="/drift/conversation/:id" element={<DriftConversationPage />} />
        </Route>

        {/* Backward compatibility redirects */}
        <Route path="/profile" element={<Navigate to="/me" replace />} />
        <Route path="/plaza" element={<Navigate to="/letters" replace />} />
        <Route path="/museum" element={<Navigate to="/letters" replace />} />
        <Route path="/courier" element={<Navigate to="/me?tab=courier" replace />} />
        <Route path="/inbox" element={<Navigate to="/letters" replace />} />

        {/* 404 catch-all */}
        <Route path="*" element={<NotFoundPage />} />

        {/* Admin routes (own layout, no Header/BottomNav) */}
        <Route
          path="/admin"
          element={
            <AdminGuard>
              <AdminLayout />
            </AdminGuard>
          }
        >
          <Route index element={<AdminDashboard />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="letters" element={<AdminLetters />} />
          <Route path="review" element={<AdminReview />} />
          <Route path="couriers" element={<AdminCouriers />} />
          <Route path="envelopes" element={<AdminEnvelopes />} />
          <Route path="reports" element={<AdminReports />} />
          <Route path="settings" element={<AdminSettings />} />
          <Route path="ai-config" element={<AdminAIConfig />} />
        </Route>
          </Routes>
        </Suspense>
        <Toaster position="top-center" richColors />
      </ErrorBoundary>
    </div>
  );
}

export default App;
